import React from 'react';
import { ProgressWidget } from '../components/ProgressWidget';
import { AchievementNotificationManager } from '../components/AchievementNotification';
import { ErrorBoundary } from '../components/ErrorBoundary';

export const ProgressPage: React.FC = () => {
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center space-x-3 mb-4">
          <div className="h-12 w-12 bg-sage-100 flex items-center justify-center">
            <span className="text-sage-600 font-bold text-xl">📊</span>
          </div>
          <div>
            <h1 className="text-4xl font-bold text-charcoal-900">
              Progress & Game Mechanics
            </h1>
            <p className="text-xl text-charcoal-600">
              Track Your Learning Journey
            </p>
          </div>
        </div>
        
        <div className="max-w-4xl mx-auto">
          <p className="text-lg text-charcoal-700 leading-relaxed">
            The Progress & Game Mechanics system transforms your learning journey into an engaging, 
            gamified experience. Track your achievements, unlock new capabilities, and see your 
            growth through the Peak Priorities knowledge system.
          </p>
        </div>
      </div>

      {/* Status Badge */}
      <div className="text-center">
        <span className="px-4 py-2 bg-[var(--mustard-dim)] text-[var(--mustard)] text-sm font-semibold rounded-full">
          🧪 Development Feature
        </span>
      </div>

      {/* Progress Widget */}
      <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-8">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Your Progress Dashboard</h2>
        <div className="min-h-[300px]">
          <ErrorBoundary componentName="ProgressWidget">
            <ProgressWidget />
          </ErrorBoundary>
        </div>
      </div>

      {/* Achievement System */}
      <div className="bg-gradient-to-r from-sage-50 to-consulting-slate-blue-50 p-8 border-2 border-sage-200">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Achievement System</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-charcoal-900">Learning Achievements</h3>
            <ul className="space-y-2">
              <li className="flex items-start space-x-2">
                <span className="text-sage-600 mt-1">🏆</span>
                <span className="text-charcoal-700">First Steps: Complete your first learning module</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-sage-600 mt-1">🎯</span>
                <span className="text-charcoal-700">Explorer: Visit 10 different pages</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-sage-600 mt-1">📚</span>
                <span className="text-charcoal-700">Scholar: Complete 5 framework deep-dives</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-sage-600 mt-1">🚀</span>
                <span className="text-charcoal-700">Master: Achieve 100% completion in a domain</span>
              </li>
            </ul>
          </div>
          
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-charcoal-900">Engagement Rewards</h3>
            <ul className="space-y-2">
              <li className="flex items-start space-x-2">
                <span className="text-consulting-slate-blue-600 mt-1">⭐</span>
                <span className="text-charcoal-700">Streak Master: Maintain 7-day learning streak</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-consulting-slate-blue-600 mt-1">💡</span>
                <span className="text-charcoal-700">Insight Hunter: Discover 20 key insights</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-consulting-slate-blue-600 mt-1">🔗</span>
                <span className="text-charcoal-700">Connector: Make 10 framework connections</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-consulting-slate-blue-600 mt-1">🎨</span>
                <span className="text-charcoal-700">Creator: Build your first custom framework</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Game Mechanics */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-8 border-2 border-[var(--mustard)]">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Game Mechanics</h2>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="h-16 w-16 bg-[var(--mustard-dim)] rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-[var(--mustard)] text-2xl">🎮</span>
            </div>
            <h3 className="font-semibold text-charcoal-900 mb-2">Experience Points</h3>
            <p className="text-charcoal-700 text-sm">
              Earn XP for every interaction, learning milestone, and achievement unlocked.
            </p>
          </div>
          
          <div className="text-center">
            <div className="h-16 w-16 bg-sage-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-sage-600 text-2xl">🏅</span>
            </div>
            <h3 className="font-semibold text-charcoal-900 mb-2">Badges & Levels</h3>
            <p className="text-charcoal-700 text-sm">
              Unlock badges for specific accomplishments and level up your expertise.
            </p>
          </div>
          
          <div className="text-center">
            <div className="h-16 w-16 bg-consulting-slate-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-consulting-slate-blue-600 text-2xl">📈</span>
            </div>
            <h3 className="font-semibold text-charcoal-900 mb-2">Progress Tracking</h3>
            <p className="text-charcoal-700 text-sm">
              Visual progress bars and completion percentages for all learning paths.
            </p>
          </div>
        </div>
      </div>

      {/* Learning Paths */}
      <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-8">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Learning Paths</h2>
        <div className="space-y-4">
          <div className="border border-[var(--rule)] p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-charcoal-900">Foundation Path</h3>
              <span className="text-sm text-charcoal-600">0/5 completed</span>
            </div>
            <div className="w-full bg-[var(--hover-tint)] rounded-full h-2">
              <div className="bg-sage-600 h-2 rounded-full" style={{ width: '0%' }}></div>
            </div>
            <p className="text-sm text-charcoal-600 mt-2">
              Master the core concepts of prioritization and business effectiveness.
            </p>
          </div>
          
          <div className="border border-[var(--rule)] p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-charcoal-900">Advanced Practitioner</h3>
              <span className="text-sm text-charcoal-600">0/8 completed</span>
            </div>
            <div className="w-full bg-[var(--hover-tint)] rounded-full h-2">
              <div className="bg-consulting-slate-blue-600 h-2 rounded-full" style={{ width: '0%' }}></div>
            </div>
            <p className="text-sm text-charcoal-600 mt-2">
              Deep dive into advanced frameworks and real-world applications.
            </p>
          </div>
          
          <div className="border border-[var(--rule)] p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-charcoal-900">Expert Level</h3>
              <span className="text-sm text-charcoal-600">0/12 completed</span>
            </div>
            <div className="w-full bg-[var(--hover-tint)] rounded-full h-2">
              <div className="bg-[var(--mustard)] h-2 rounded-full" style={{ width: '0%' }}></div>
            </div>
            <p className="text-sm text-charcoal-600 mt-2">
              Become a master of prioritization and organizational effectiveness.
            </p>
          </div>
        </div>
      </div>

      {/* Development Notes */}
      <div className="bg-[var(--mustard-dim)] border border-[var(--mustard)] p-6">
        <h3 className="text-lg font-semibold text-[var(--mustard)] mb-3">Development Status</h3>
        <p className="text-[var(--mustard)] text-sm leading-relaxed">
          The Progress & Game Mechanics system is currently in active development as part of The Lab. 
          This feature will transform the learning experience by adding gamification elements, 
          achievement tracking, and personalized progress visualization to make the journey through 
          the Peak Priorities knowledge system more engaging and motivating.
        </p>
      </div>

      {/* Achievement Notifications */}
      <AchievementNotificationManager />
    </div>
  );
};
